import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import { Markdown } from "../Markdown";
import { ErrorBox, Spinner } from "../Spinner";
import {
  buildTranscriptWithStats,
  formatElapsed,
  hiddenBreakdown,
  pretty,
  type ToolItem,
  type TxItem,
} from "./transcriptModel";

function Tool({
  title,
  detail,
  status,
  error,
  children,
}: {
  title: ReactNode;
  detail?: string;
  status?: string;
  error?: boolean;
  children?: ReactNode;
}) {
  return (
    <details className={`tx-tool ${error ? "tx-tool-error" : ""}`}>
      <summary>
        <span className="tx-tool-title">{title}</span>
        {detail && <span className="tx-tool-detail">{detail}</span>}
        {status && <span className="tx-tool-status">{status}</span>}
      </summary>
      {children}
    </details>
  );
}

function Pre({ label, text }: { label?: string; text?: string }) {
  if (!text?.trim()) return null;
  return (
    <>
      {label && <div className="tx-section-label">{label}</div>}
      <pre className="tx-pre">{text}</pre>
    </>
  );
}

function toolStatus(t: ToolItem): string | undefined {
  const parts: string[] = [];
  if (t.children.length) parts.push(`${t.children.length} step${t.children.length === 1 ? "" : "s"}`);
  if (t.elapsedSeconds !== undefined) parts.push(`${t.pending ? "running " : ""}${formatElapsed(t.elapsedSeconds)}`);
  else if (t.pending) parts.push("running");
  if (t.error) parts.push("error");
  return parts.length ? parts.join(" · ") : undefined;
}

function TxItemView({ item }: { item: TxItem }) {
  switch (item.kind) {
    case "text":
      return (
        <div className={item.role === "user" ? "tx-user" : ""}>
          <Markdown className="tx-text">{item.text}</Markdown>
        </div>
      );
    case "thinking":
      return (
        <Tool title={item.title}>
          <Pre text={item.text} />
        </Tool>
      );
    case "tool": {
      const input = item.input === undefined ? undefined : pretty(item.input);
      return (
        <Tool title={item.name} detail={item.detail ?? item.description} status={toolStatus(item)} error={item.error}>
          {item.description && item.detail && <div className="tx-system">{item.description}</div>}
          <Pre label={item.result !== undefined ? "Input" : undefined} text={input} />
          <Pre label={input !== undefined ? "Output" : undefined} text={item.result} />
          {item.children.length > 0 && (
            <div className="tx-children">
              {item.children.map((c) => (
                <TxItemView key={c.key} item={c} />
              ))}
            </div>
          )}
        </Tool>
      );
    }
    case "system":
      return (
        <div className={`tx-system ${item.error ? "tx-error" : ""}`}>
          {item.text}
          {item.count > 1 ? ` ×${item.count}` : ""}
        </div>
      );
    case "raw":
      return <div className={`tx-raw tx-raw-${item.stream}`}>{item.text}</div>;
    case "hidden":
      return (
        <details className="tx-tool tx-hidden">
          <summary>
            {item.events.length} hidden event{item.events.length === 1 ? "" : "s"} ({hiddenBreakdown(item.events)})
          </summary>
          {item.events.map((ev) => (
            <div key={ev.seq} className={`tx-raw tx-raw-${ev.stream}`}>
              {ev.raw}
            </div>
          ))}
        </details>
      );
  }
}

export function Transcript({ runId }: { runId: string }) {
  const [raw, setRaw] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: qk.runEvents(runId),
    queryFn: () => api.runEvents(runId),
  });
  const built = useMemo(() => (data ? buildTranscriptWithStats(data) : null), [data]);
  if (isLoading) return <Spinner label="Loading transcript…" />;
  if (error) return <ErrorBox error={error} />;
  if (!data?.length || !built) return <p className="muted small">No transcript events yet.</p>;
  const hidden = built.stats.hidden + built.stats.folded;
  return (
    <div className="transcript">
      <div className="transcript-toolbar">
        <span className="muted small">
          {data.length} events{!raw && hidden ? ` · ${hidden} hidden` : ""}
        </span>
        <label className="checkbox small">
          <input type="checkbox" checked={raw} onChange={(e) => setRaw(e.target.checked)} /> Raw
        </label>
      </div>
      <div className="transcript-body">
        {raw
          ? data.map((ev) => (
              <div key={ev.seq} className={`tx-raw tx-raw-${ev.stream}`}>
                {ev.raw}
              </div>
            ))
          : built.items.map((item) => <TxItemView key={item.key} item={item} />)}
      </div>
    </div>
  );
}
